import { get } from 'svelte/store';
import { percent_from_value_range, value_from_percent_range } from '../../../../helpers/utilities';
import { format } from '../../../../services/i18n';
import type { SelectOptionPropsType } from '@Shelly/shelly-design-system';
import { getTimeFormat } from 'src/helpers/date_helper';

let _format = get(format);

//#region helpers
function daysToNumbers(days) {
	return days.map((day) => Number(day));
}

export const modeTypes = {
	time: 'Time',
	sunset: 'Sunset',
	sunrise: 'Sunrise',
};

export function time_dots(time) {
	//this function will add dots to time value
	time = time.split('');
	time.splice(2, 0, ':');
	time = time.join('');
	return time;
}

export function time_dots_removal(time) {
	//this function will remove dots from time value
	time = time.split(':');
	time = time.join('');
	return time;
}
//#endregion

//#region income data formatting
export const scheduleDataFormatReceive = (scheduleRules, tempRange, device, units?) => {
	let formatted_list = [];

	scheduleRules.forEach((schedule) => {
		let output,
			time,
			days,
			brightness,
			colorTemperature,
			selection,
			duration,
			mode,
			sun,
			rollerHeight,
			rollerState;

		output = schedule.split('-');
		time = time_dots(output[0]);

		if (time.length > 5) {
			sun = time.slice(-3);
			time = time.slice(0, 5);
			if (sun.includes('ss')) {
				mode = 'Sunset';
			}
			if (sun.includes('sr')) {
				mode = 'Sunrise';
			}
			if (sun.includes('b')) {
				time = `-${time}`;
			}
		} else {
			mode = 'Time';
		}

		days = daysToNumbers(output[1].split(''));

		if (device.category !== 'roller') {
			brightness = output[2];
			brightness.replace('%', '');
			if (brightness.includes(';')) {
				let values = brightness.split(';');
				if (values[0].includes('@')) {
					let temp_value = values[0].split('@');

					duration = temp_value[0];

					brightness = temp_value[1];
				} else {
					if (isNaN(+values[0])) {
						selection = values[0];
						brightness = '';
					} else {
						brightness = values[0];
					}
					// duration = '0';//this is how to hide duration from labels if it is 0
				}

				values[1] !== '101'
					? (colorTemperature = value_from_percent_range(values[1], [
							tempRange[0],
							tempRange[1],
						]))
					: '';

				selection = values[2];

				output = {
					mode,
					sun,
					time,
					days,
					brightness,
					colorTemperature,
					selection,
					duration,
				};
			} else if (brightness.includes('@')) {
				let tempValue = brightness.split('@');

				duration = tempValue[0];

				brightness = tempValue[1];

				if (isNaN(+brightness.replace('%', ''))) {
					selection = brightness;
					brightness = undefined;
				}

				output = { mode, sun, time, days, brightness, selection, duration };
			} else {
				if (brightness === 'on' || brightness === 'off' || brightness === '') {
					selection = brightness;
					brightness = undefined;
					output = { selection, mode, sun, time, days };
				} else {
					if (device?.mode === 'thermostat') {
						brightness = `${brightness} °${units}`;
					}
					output = { selection, mode, sun, time, days, brightness };
				}
			}
		} else {
			if (output[2].includes('%')) {
				rollerHeight = output[2].replace('%', '');
				rollerState = '101';
			} else {
				rollerHeight = '101';
				rollerState = output[2];
			}
			output = { rollerHeight, rollerState, mode, sun, time, days };
		}
		formatted_list.push(output);
	});
	return formatted_list;
};
//#endregion

//#region edit data
export const editSchedule = (schedules, rule?, index?) => {
	if (typeof index !== 'number') {
		return schedules + `,${rule}`;
	} else {
		schedules.forEach((_, i) => {
			if (index === i) {
				schedules[i] = rule;
			}
		});
		return schedules.join(',');
	}
};

export const scheduleDelete = async (schedules, index) => {
	//this function removes chosen schedule by index
	if (schedules.length) {
		schedules.splice(index, 1);
		schedules = schedules;
	}
	return schedules;
};
//#endregion

//#region outcome data formatting
export const scheduleDataFormatSent = async (data, device) => {
	//this function translate selected user data to
	//a string which will be sent back to devices

	let {
		time,
		days,
		brightness,
		colorTemperature,
		duration,
		selection,
		mode,
		rollerHeight,
		rollerState,
	} = await data;

	let hour = time.split(':')[0];
	let negative = hour.toString().includes('-');

	switch (mode) {
		case 'Sunrise':
			if (!negative) {
				mode = 'asr';
			}
			if (negative) {
				mode = 'bsr';
				time = time.replace('-', '');
			}
			break;
		case 'Sunset':
			if (!negative) {
				mode = 'ass';
			}
			if (negative) {
				mode = 'bss';
				time = time.replace('-', '');
			}
			break;
		default:
			mode = '';
			break;
	}

	selection !== '101' ? selection : (selection = '');
	if (data.optional) {
		if (typeof colorTemperature === 'undefined' && typeof brightness !== 'undefined') {
			brightness = brightness.toString();
			if (
				(selection === 'on' && brightness.replace('%', '').length === 0) ||
				typeof brightness === 'undefined' ||
				brightness === '101'
			) {
				brightness = '';
			} else if (selection === 'off') {
				brightness = '';
			} else {
				selection = '';
				brightness = `${brightness.replace('%', '')}%`;
			}
		}

		if (colorTemperature !== '101' && typeof colorTemperature !== 'undefined') {
			if (typeof brightness !== 'undefined') {
				brightness = `${brightness};`;
			} else {
				brightness = '0;';
			}

			colorTemperature = `${percent_from_value_range(colorTemperature, [data.tempRange[0], data.tempRange[1]])};`;
		} else if (colorTemperature === '101') {
			colorTemperature = `${colorTemperature};`;
			brightness = `${brightness};`;
		} else {
			colorTemperature = '';
		}
	} else {
		colorTemperature = '';
	}
	if (typeof brightness === 'undefined') brightness = '';
	duration !== '101' && duration !== undefined && duration !== '0' && duration !== 0
		? (duration = `${duration}@`)
		: (duration = '');

	time = time_dots_removal(time);
	if (device.category !== 'roller') {
		return `${time}${mode}-${days.join('')}-${duration}${brightness}${colorTemperature}${selection}`;
	} else {
		if (rollerHeight !== '101' || rollerState === '101') {
			// if (!rollerHeight.includes('%')) {
			rollerHeight += '%';
			// }
		} else if (
			rollerHeight === '101' &&
			rollerState !== '101' &&
			typeof rollerState !== 'undefined'
		) {
			rollerState = rollerState;
		}
		return `${time}${mode}-${days.join('')}-${
			rollerHeight !== '101' && typeof rollerHeight !== 'undefined'
				? rollerHeight
				: rollerState !== '101' && typeof rollerState !== 'undefined'
					? rollerState
					: ''
		}`;
	}
};

export const combineLists = (data, tempRange, device, enabled = true) => {
	//this function groups icons with commands, decides which sun icon to be displayed
	let list = [];

	let sunIcon = {
		Sunrise: 'fa-solid fa-sunrise',
		Sunset: 'fa-solid fa-sunset',
	};

	//some trv devices returns
	// device.mode = ''
	// device.category = 'thermostat'
	// and label list showing temperature as brightness
	let deviceMode =
		device?.mode === '' && device?.category === 'thermostat' ? 'thermostat' : device.mode;

	let icons = {
		time: 'fa-solid fa-clock',
		brightness:
			deviceMode !== 'thermostat'
				? 'fa-solid fa-brightness'
				: 'fa-solid fa-thermometer-three-quarters',
		colorTemperature: 'fa-solid fa-sun',
		temperature: 'fa-solid fa-thermometer-three-quarters',
		selection: 'fa-solid fa-toggle-on',
		duration: 'fa-solid fa-history',
		sun: sunIcon,
	};

	Object.entries(data).forEach((option) => {
		option[0] === 'colorTemperature' && option[1] !== undefined
			? (option[1] = percent_from_value_range(option[1], [tempRange[0], tempRange[1]]))
			: '';

		if (data.mode !== 'Time') {
			if (data.mode === 'Sunrise') {
				if (data.sun.includes('bs')) {
					if (option[0] === 'time') {
						list.push({ value: option[1], icon: icons[option[0]] });
					}
				}
				if (data.sun.includes('as')) {
					if (option[0] === 'time') {
						list.push({ value: '+' + option[1], icon: icons[option[0]] });
					}
				}
			}
			if (data.mode === 'Sunset') {
				if (data.sun.includes('bs')) {
					if (option[0] === 'time') {
						list.push({ value: option[1], icon: icons[option[0]] });
					}
				}
				if (data.sun.includes('as')) {
					if (option[0] === 'time') {
						list.push({ value: '+' + option[1], icon: icons[option[0]] });
					}
				}
			}
		} else {
			if (option[0] === 'time') {
				const time = option[1];
				let hours = Number(time.split(':')[0]);
				let mins = time.split(':')[1];
				list.push({
					value: getTimeFormat(hours, mins),
					icon: icons[option[0]],
				});
			}
		}
		if (option[0] === 'sun' && option[1] !== undefined) {
			if (option[1].includes('ss')) {
				list.push({
					value: _format('schedule_opt_sunset'),
					icon: 'fa-solid fa-sunset',
				});
			}
			if (option[1].includes('sr')) {
				list.push({
					value: _format('schedule_opt_sunrise'),
					icon: 'fa-solid fa-sunrise',
				});
			}
		}
		if (option[0] === 'rollerHeight' && option[1] !== '101') {
			list.push({ value: `${option[1]}%`, icon: 'fa-solid fa-blinds' });
		} else if (option[0] === 'rollerState' && option[1] !== '101') {
			// if(option[1]==='open'){
			// 	list.push({ value: option[1] , icon: faBlindsRaised });
			// } else if(option[1]==='close') {
			list.push({
				value: _format(`schedule_action_${option[1]}`),
				icon: option[1] === 'open' ? 'fa-solid fa-blinds-raised' : 'fa-solid fa-blinds',
			});
			// }
		}
		if (['on', 'off'].includes(option[1])) {
			list.push({
				value: _format(`schedule_action_${option[1]}`),
				icon: option[1] === 'on' ? 'fa-solid fa-toggle-on' : 'fa-solid fa-toggle-off',
			});
		}
		if (
			option[1] !== 'on' &&
			option[1] !== 'off' &&
			option[1] !== undefined &&
			option[1] !== '101' &&
			option[1] !== '' &&
			option[0] !== 'mode' &&
			option[0] !== 'rollerHeight' &&
			option[0] !== 'rollerState' &&
			option[0] !== 'days' &&
			option[0] !== 'sun' &&
			option[0] !== 'time'
		) {
			list.push({ value: option[1], icon: icons[option[0]] });
		}
	});

	if (!enabled) {
		list.forEach((label) => {
			label.type = 'info';
		});
	}
	return list;
};

//#endregion

//#region non repeatable elements
export const unify_schedules = (schedules) => {
	return [...new Set(schedules.split(','))].join(',');
};
//#endregion

export const getHoursOptions = (minHours = 0, maxHours = 23) => {
	let options: SelectOptionPropsType[] = [];
	for (let index = minHours; index <= maxHours; index++) {
		if (index > 23) {
			break;
		}
		const prefix = index < 10 ? '0' : '';
		options = [
			{
				title: `${prefix}${index} h`,
				value: index,
			},
			...options,
		];
	}

	return options.reverse();
};

export const getMinOptions = (minMins = 0, maxMins = 59) => {
	let options: SelectOptionPropsType[] = [];
	for (let index = minMins; index <= maxMins; index++) {
		if (index > 59) {
			break;
		}
		const prefix = index < 10 ? '0' : '';
		options = [
			{
				title: `${prefix}${index} min`,
				value: index,
			},
			...options,
		];
	}

	return options.reverse();
};
